/**
 * ConversationList — left panel with status filter pills, search, and rows.
 *
 * Props are plain values (not signals). State is managed by the Stimulus controller.
 */

import { h, Fragment } from 'preact'
import { useRef, useEffect } from 'preact/hooks'
import { ConversationListRow } from './ConversationListRow.jsx'
import { t } from './support-i18n.js'

// Status tab definitions — label and icon resolved at render time via i18n
const STATUS_TABS = [
  { key: null,                   icon: 'bi-inbox',           labelKey: 'support.inbox.status_all' },
  { key: 'open',                 icon: 'bi-circle',          labelKey: 'support.inbox.status_open' },
  { key: 'inprogress',           icon: 'bi-arrow-repeat',    labelKey: 'support.inbox.status_inprogress' },
  { key: 'waiting_for_customer', icon: 'bi-hourglass-split', labelKey: 'support.inbox.status_waiting' },
  { key: 'closed',               icon: 'bi-check-circle',    labelKey: 'support.inbox.status_closed' },
]

export function ConversationList({
  conversations = [],
  statusFilter = null,
  searchQuery = '',
  selectedId = null,
  statusCounts = {},
  mailboxes = [],
  currentMailboxId = null,
  onSelectConversation,
  onStatusFilter,
  onSearch,
  onLoadMore,
  onMailboxChange,
  loading = false,
}) {
  const list        = Array.isArray(conversations) ? conversations : []
  const counts      = statusCounts || {}
  const sentinelRef = useRef(null)

  // Infinite scroll sentinel
  useEffect(() => {
    if (!sentinelRef.current || !onLoadMore) return
    const observer = new IntersectionObserver(
      entries => { if (entries[0].isIntersecting) onLoadMore() },
      { rootMargin: '120px' }
    )
    observer.observe(sentinelRef.current)
    return () => observer.disconnect()
  }, [sentinelRef.current, onLoadMore])

  const totalCount = Object.values(counts).reduce((a, b) => a + b, 0)

  return (
    <div class="support-list-panel">

      {/* List header */}
      <div class="list-header">
        <h6>{t('support.inbox.title')}</h6>
        {mailboxes.length >= 1 && (
          <div class="list-mailbox-filter">
            <select
              class="form-select form-select-sm"
              value={currentMailboxId ?? ''}
              onChange={e => onMailboxChange?.(e.target.value ? Number(e.target.value) : null)}
            >
              <option value="">{t('support.inbox.all_mailboxes', 'Minden postafiók')}</option>
              {mailboxes.map(mb => (
                <option key={mb.id} value={mb.id}>{mb.name}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Search */}
      <div class="list-search">
        <i class="bi bi-search" />
        <input
          type="search"
          placeholder={t('support.inbox.search_placeholder')}
          value={searchQuery}
          onInput={e => onSearch?.(e.target.value)}
        />
      </div>

      {/* Status filter pills */}
      <div class="list-filters">
        {STATUS_TABS.map(tab => {
          const count    = tab.key ? (counts[tab.key] || 0) : totalCount
          const isActive = statusFilter === tab.key
          return (
            <button
              key={tab.key ?? 'all'}
              type="button"
              class={`status-pill${isActive ? ' active' : ''}`}
              onClick={() => onStatusFilter?.(tab.key)}
              title={t(tab.labelKey)}
            >
              <i class={`bi ${tab.icon} status-pill-icon`} />
              <span class="pill-count">{count}</span>
            </button>
          )
        })}
      </div>

      {/* Conversation rows */}
      <div class="list-scroll" id="support_conversation_list">
        {loading && list.length === 0
          ? (
            <div class="support-loading list-loading">
              <div class="spinner-border spinner-border-sm support-spinner-md" role="status" />
              <span>{t('support.inbox.load_more')}</span>
            </div>
          )
          : list.length === 0
            ? (
              <div class="support-empty compact">
                <i class="bi bi-inbox" />
                <p>{searchQuery ? t('support.inbox.no_results') : t('support.inbox.empty')}</p>
              </div>
            )
            : (
              <Fragment>
                {list.map(conv => (
                  <ConversationListRow
                    key={conv.id}
                    conversation={conv}
                    isActive={conv.id === selectedId}
                    onClick={() => onSelectConversation?.(conv.id)}
                  />
                ))}
                <div ref={sentinelRef} class="py-1">
                  {loading && (
                    <div class="support-loading loadmore-loading">
                      <div class="spinner-border spinner-border-sm support-spinner-md" role="status" />
                    </div>
                  )}
                </div>
              </Fragment>
            )
        }
      </div>

    </div>
  )
}
